import { createAppRequest } from '@/services/request';
import type {
  FundFormValues,
  InvestmentFundPhase,
  InvestmentFundRecord,
} from '@/types/fund';

const request = createAppRequest();
const BASE_PATH = '/bff/investment/funds';

export type FundAuditResponse = {
  lifecycle: Array<{
    uuid: string;
    action: string;
    afterJson?: Record<string, unknown> | null;
    createdAt: string;
  }>;
  setup: Array<{
    uuid: string;
    stage: string;
    title: string;
    status: string;
    result?: string | null;
    filledAt?: string | null;
  }>;
};

export const fundApi = {
  /** 基金列表（includeInactive=true 时包含停用基金）。 */
  listFunds: (params?: { includeInactive?: boolean; controlType?: '自管' | '参股' }) =>
    request<InvestmentFundRecord[]>(BASE_PATH, {
      method: 'GET',
      params,
    }),

  getFund: (uuid: string) =>
    request<InvestmentFundRecord>(`${BASE_PATH}/${uuid}`, {
      method: 'GET',
    }),

  /** 备案联动提醒（G-05）。 */
  filingReminder: (uuid: string) =>
    request<{
      fundUuid: string;
      registrationType: string | null;
      required: boolean;
      filingConfirmed: boolean;
      filingNo: string | null;
      reminder: string | null;
    }>(`${BASE_PATH}/${uuid}/filing-reminder`, {
      method: 'GET',
    }),

  audit: (uuid: string) =>
    request<FundAuditResponse>(`${BASE_PATH}/${uuid}/audit`, { method: 'GET' }),

  createFund: (payload: FundFormValues) =>
    request<InvestmentFundRecord>(BASE_PATH, {
      method: 'POST',
      data: { currency: 'CNY', ...payload },
    }),

  updateFund: (uuid: string, payload: Partial<FundFormValues>) =>
    request<InvestmentFundRecord>(`${BASE_PATH}/${uuid}`, {
      method: 'PATCH',
      data: payload,
    }),

  /** 推进基金生命周期阶段（后端校验只能按序相邻前进）。 */
  changePhase: (uuid: string, phase: InvestmentFundPhase) =>
    request<InvestmentFundRecord>(`${BASE_PATH}/${uuid}/phase`, {
      method: 'PATCH',
      data: { phase },
    }),
  startRaising: (uuid: string, remark?: string) =>
    request<InvestmentFundRecord>(`${BASE_PATH}/${uuid}/lifecycle/raising`, { method: 'POST', data: { remark } }),
  confirmFirstClose: (uuid: string, remark?: string) =>
    request<InvestmentFundRecord>(`${BASE_PATH}/${uuid}/lifecycle/first-close`, { method: 'POST', data: { remark } }),
  confirmFinalClose: (uuid: string, remark?: string) =>
    request<InvestmentFundRecord>(`${BASE_PATH}/${uuid}/lifecycle/final-close`, { method: 'POST', data: { remark } }),
  startExit: (uuid: string, remark?: string) =>
    request<InvestmentFundRecord>(`${BASE_PATH}/${uuid}/lifecycle/exit`, { method: 'POST', data: { remark } }),

  /** 启用 / 停用基金；已启动清算的基金将被后端拒绝。 */
  toggleStatus: (uuid: string, status: '启用' | '停用') =>
    request<InvestmentFundRecord>(`${BASE_PATH}/${uuid}/status`, {
      method: 'PATCH',
      data: { status },
    }),

  /** 软删除基金；仅允许已停用或清算完成的基金才能删除。 */
  softDelete: (uuid: string) =>
    request<void>(`${BASE_PATH}/${uuid}`, {
      method: 'DELETE',
    }),

  /** 退出回款待分配列表。 */
  listPendingDistributions: (params?: { fundUuid?: string }) =>
    request<
      Array<{
        id: string;
        fundUuid: string;
        exitTransactionUuid: string;
        portfolioCompanyUuid: string;
        grossProceeds: number;
        netProceeds: number;
        realizedGain: number;
        status: string;
        completedAt?: string;
        allowedNextStatuses?: string[];
      }>
    >(`${BASE_PATH}/pending-distributions`, {
      method: 'GET',
      params,
    }),

  /** 推进待分配状态。 */
  updatePendingDistributionStatus: (
    uuid: string,
    data: { status: '待分配' | '已分配' | '已取消'; remark?: string },
  ) =>
    request(`${BASE_PATH}/pending-distributions/${uuid}/status`, {
      method: 'PATCH',
      data,
    }),
};
